/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Adjusts the css top property of an element such that it sticks appropriately based on the scroll
 * direction. The container is assumed to be position: sticky, with top: 0 (or whatever). When
 * scrolling down, it'll stick to the bottom of the container, when scrolling up it'll stick to
 * the top of the container.
 *
 * FansBRANDS® responsive extension:
 * - iPad/tablet portrait (750–1024 px) is intentionally treated as non-sticky/mobile-like layout.
 * - orientation changes inside the same theme breakpoint are handled explicitly.
 * - stale sticky height/top state is reset when sticky mode is disabled.
 */
if (!customElements.get('sticky-scroll-direction')) {
  class StickyScrollDirection extends HTMLElement {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      this.container = this.firstElementChild;
      if (!this.container) return;

      this.minStickySize = this.dataset.minStickySize;
      this.listenersActive = false;

      this.scrollListener = this.scrollListener || this.handleScroll.bind(this);
      this.toggleListenersHandler = this.toggleListenersHandler || this.toggleListeners.bind(this);
      this.viewportChangeHandler = this.viewportChangeHandler
        || debounce(this.handleViewportChange.bind(this), 100);

      this.updateHeaderMetrics();
      this.resetCurrentTop();

      window.addEventListener('on:breakpoint-change', this.toggleListenersHandler);
      window.addEventListener('resize', this.viewportChangeHandler, { passive: true });
      window.addEventListener('orientationchange', this.viewportChangeHandler, { passive: true });

      this.toggleListeners();
    }

    updateHeaderMetrics() {
      const headerIsSticky = document.querySelector('store-header[data-is-sticky="true"]');
      const rawHeaderHeight = getComputedStyle(this.parentElement)
        .getPropertyValue('--header-height')
        .replace('px', '');

      const parsedHeaderHeight = Number.parseInt(rawHeaderHeight, 10);
      this.headerHeight = Number.isFinite(parsedHeaderHeight) ? parsedHeaderHeight : 0;
      this.defaultTop = headerIsSticky ? this.headerHeight + 30 : 48;
    }

    resetCurrentTop() {
      this.scrollY = window.scrollY || window.pageYOffset || 0;

      const rect = this.getBoundingClientRect();
      const calculatedTop = Number.parseInt(
        (document.documentElement.scrollTop || 0) + rect.top - this.offsetTop,
        10
      );

      this.currentTop = Number.isFinite(calculatedTop) ? calculatedTop : this.defaultTop;
    }

    isPortraitTablet() {
      return window.matchMedia(
        '(min-width: 750px) and (max-width: 1024px) and (orientation: portrait)'
      ).matches;
    }

    meetsThemeBreakpoint() {
      if (this.minStickySize === 'lg') return Boolean(theme.mediaMatches.lg);
      if (this.minStickySize === 'md') return Boolean(theme.mediaMatches.md);
      return true;
    }

    shouldEnableSticky() {
      return this.meetsThemeBreakpoint() && !this.isPortraitTablet();
    }

    /**
     * Either inits or destroys the component based on screen size/orientation.
     */
    toggleListeners() {
      if (this.shouldEnableSticky()) {
        this.addListeners();
      } else {
        this.removeListeners();
      }
    }

    handleViewportChange() {
      this.updateHeaderMetrics();

      const shouldEnable = this.shouldEnableSticky();

      if (!shouldEnable) {
        this.removeListeners();
        return;
      }

      if (!this.listenersActive) {
        this.resetCurrentTop();
        this.addListeners();
      } else {
        this.resetCurrentTop();
        this.setStickyHeight();
        this.handleScroll();
      }
    }

    /**
     * Init the scroll watching.
     */
    addListeners() {
      if (this.listenersActive || !this.container) return;

      this.listenersActive = true;
      window.addEventListener('scroll', this.scrollListener, { passive: true });

      // Set the height of the parent to the total height of other elements.
      if (this.parentElement.dataset.stickyHeightElems) {
        this.debouncedSetStickyHeights = this.debouncedSetStickyHeights
          || debounce(this.setStickyHeight.bind(this), 250);

        window.addEventListener('resize', this.debouncedSetStickyHeights, { passive: true });

        if ('MutationObserver' in window) {
          this.stickyHeightElems = this.parentElement.dataset.stickyHeightElems;

          if (this.observer) this.observer.disconnect();

          this.observer = new MutationObserver(this.debouncedSetStickyHeights);
          document.querySelectorAll(this.stickyHeightElems).forEach((elem) => {
            this.observer.observe(elem, {
              childList: true,
              attributes: true,
              subtree: true
            });
          });
        }

        this.setStickyHeight();
      }

      // Listen for the opening of disclosures.
      this.disclosures = this.querySelectorAll('details');
      this.disclosureChangeHandler = this.disclosureChangeHandler
        || this.handleDisclosureChange.bind(this);

      this.disclosures.forEach((disclosure) => {
        disclosure.addEventListener('transitionend', this.disclosureChangeHandler);
      });

      this.handleScroll();
    }

    /**
     * Handle the opening/closing of disclosures when they extend beyond the viewport.
     * @param {object} evt - Event object.
     */
    handleDisclosureChange(evt) {
      if (evt.target.classList.contains('disclosure__panel')) {
        const disclosure = evt.target.closest('.disclosure');
        const summaryElem = disclosure ? disclosure.querySelector('summary') : null;

        if (summaryElem && !theme.elementUtil.isInViewport(summaryElem)) {
          window.scrollTo({
            behavior: 'smooth',
            top: summaryElem.getBoundingClientRect().top
              - document.body.getBoundingClientRect().top
              - this.headerHeight
              - 20
          });
        }
      }
    }

    /**
     * Destroy listeners and reset CSS state.
     */
    removeListeners() {
      if (this.scrollListener) {
        window.removeEventListener('scroll', this.scrollListener);
      }

      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      if (this.debouncedSetStickyHeights) {
        window.removeEventListener('resize', this.debouncedSetStickyHeights);
      }

      if (this.disclosures && this.disclosureChangeHandler) {
        this.disclosures.forEach((disclosure) => {
          disclosure.removeEventListener('transitionend', this.disclosureChangeHandler);
        });
      }

      if (this.container) {
        this.container.style.removeProperty('top');
      }

      if (this.parentElement) {
        this.parentElement.style.removeProperty('--sticky-height');
      }

      this.listenersActive = false;
      this.resetCurrentTop();
    }

    destroy() {
      this.removeListeners();

      if (this.toggleListenersHandler) {
        window.removeEventListener('on:breakpoint-change', this.toggleListenersHandler);
      }

      if (this.viewportChangeHandler) {
        window.removeEventListener('resize', this.viewportChangeHandler);
        window.removeEventListener('orientationchange', this.viewportChangeHandler);
      }
    }

    /**
     * Calculates the heights of certain elements that the height of the sticky element depends on.
     */
    setStickyHeight() {
      if (!this.listenersActive || !this.parentElement.dataset.stickyHeightElems) return;

      const selector = this.parentElement.dataset.stickyHeightElems;
      let totalHeight = 0;

      document.querySelectorAll(selector).forEach((elem) => {
        totalHeight += elem.getBoundingClientRect().height;
      });

      this.parentElement.style.setProperty(
        '--sticky-height',
        `${Math.max(0, Math.round(totalHeight))}px`
      );
    }

    /**
     * Updates the current css top based on scroll direction.
     */
    handleScroll() {
      if (!this.listenersActive || !this.container || this.isPortraitTablet()) return;

      const bounds = this.container.getBoundingClientRect();
      const currentWindowScrollY = window.scrollY || window.pageYOffset || 0;
      const maxTop = bounds.top + currentWindowScrollY - this.container.offsetTop + this.defaultTop;
      const minTop = Math.max(0, this.container.clientHeight - window.innerHeight);

      if (currentWindowScrollY < this.scrollY) {
        this.currentTop -= currentWindowScrollY - this.scrollY;
      } else {
        this.currentTop += this.scrollY - currentWindowScrollY;
      }

      this.currentTop = Math.min(
        Math.max(this.currentTop, -minTop),
        maxTop,
        this.defaultTop
      );

      this.scrollY = currentWindowScrollY;
      this.container.style.top = `${this.currentTop}px`;
    }
  }

  customElements.define('sticky-scroll-direction', StickyScrollDirection);
}
